/**
 * Size the loan by EITHER requested leverage (%) OR an initial loan amount ($)
 * — RTL and GUC sizers (owner 2026-10-19).
 *
 * The backend takes either: `_normalize_rtl_scenario` derives the advance as
 * `sizerInitialRequestedLeverage × basis` ONLY when no `baseLoanAmount` is sent,
 * and derives the holdback and total from whichever advance it ends up with. So
 * the two are one question with two ways to answer it, and posting both would
 * let the amount silently win over a leverage the user can still see.
 *
 * The choice lives in the form-only `_sizeBy` ("leverage" | "amount"), stripped
 * from every payload (`stripFormOnlyKeys`) and re-derived on resume
 * (`sizeByFor`). Switching clears the other input, so exactly one is posted.
 *
 * `sizing` is the last response's `result.sizing` — both sides of the pair, the
 * one the user did not drive CALCULATED server-side. It is shown as a
 * "last priced" line rather than written into the other field, which stays
 * blank so only one answer is ever posted.
 *
 * Rendered as ONE panel: the question ("How do you want to size the loan?") as
 * two choice cards on the left, the matching input on the right — so the pick
 * and the number read as one answer rather than a bare toggle beside a field.
 */
import Box from "@mui/material/Box";
import { useField, useForm } from "react-final-form";

import Txt from "../../components/typography/Txt";
import { FieldLabel, RffMoney, RffPercent, SZ } from "./sizerUi";
import { fmtMoney, fmtPct } from "./sizerFormat";

export const SIZE_BY_FIELD = "_sizeBy";
export const SIZE_BY = { LEVERAGE: "leverage", AMOUNT: "amount" };

const hasValue = (v) => v != null && v !== "";

/** A stored / seeded scenario → which way it was sized (leverage wins a tie). */
export const sizeByFor = (scenario) =>
  !hasValue(scenario?.sizerInitialRequestedLeverage) && hasValue(scenario?.baseLoanAmount)
    ? SIZE_BY.AMOUNT
    : SIZE_BY.LEVERAGE;

const QUESTION = "Size the loan by";

// The cards and the input share ONE height so the row reads as a single line.
const ROW_H = 56;

/** One choice card — a glyph badge, a title and a short "what you'll enter". */
const ChoiceCard = ({ on, glyph, title, desc, onClick }) => (
  <Box
    component="button"
    type="button"
    role="radio"
    aria-checked={on}
    onClick={onClick}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: "12px",
      width: "100%",
      height: ROW_H,
      minWidth: 0,
      px: "14px",
      textAlign: "left",
      fontFamily: "inherit",
      cursor: "pointer",
      borderRadius: SZ.radiusSm,
      border: `1px solid ${on ? SZ.teal600 : SZ.lineStrong}`,
      background: on ? SZ.teal50 : SZ.card,
      boxShadow: on ? `inset 0 0 0 1px ${SZ.teal600}` : "none",
      transition: "border-color .12s, background .12s, box-shadow .12s",
      "&:hover": on ? {} : { borderColor: SZ.lineStrong },
      "&:focus-visible": {
        outline: "none",
        boxShadow: "0 0 0 3px rgba(20,160,170,.25)",
      },
    }}
  >
    <Box
      sx={{
        width: 28,
        height: 28,
        flexShrink: 0,
        borderRadius: "6px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 13,
        fontWeight: 700,
        color: on ? "#fff" : SZ.ink600,
        background: on ? SZ.teal600 : "#eef2f3",
        transition: "background .12s, color .12s",
      }}
    >
      {glyph}
    </Box>
    <Box sx={{ minWidth: 0, flex: 1, display: "flex", flexDirection: "column", gap: "1px" }}>
      <Txt
        sx={{
          fontSize: 13,
          fontWeight: 600,
          color: on ? SZ.teal700 : SZ.ink900,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {title}
      </Txt>
      <Txt
        sx={{
          fontSize: 12,
          color: SZ.ink500,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {desc}
      </Txt>
    </Box>
  </Box>
);

/** The note under the row — an info glyph and plain text, key terms in bold. */
const InfoNote = ({ children }) => (
  <Box
    sx={{
      gridColumn: "1 / -1",
      display: "flex",
      alignItems: "flex-start",
      gap: "8px",
      pt: "12px",
      borderTop: `1px dashed ${SZ.line}`,
      fontSize: 12,
      lineHeight: 1.5,
      color: SZ.ink500,
      "& b": { fontWeight: 600, color: SZ.ink700 },
    }}
  >
    <Box component="span" sx={{ display: "inline-flex", mt: "2px", color: SZ.ink400, flexShrink: 0 }}>
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 16v-4M12 8h.01" />
      </svg>
    </Box>
    <Box component="span">{children}</Box>
  </Box>
);

// Taller, larger-type control so the input matches the cards beside it.
const INPUT_SX = { height: ROW_H, fontSize: 15 };

/** ", including $X financed interest reserve and $Y financed closing costs". */
const includedLine = (sizing) => {
  const parts = [
    Number(sizing.capitalizedInterestReserve) > 0
      ? `${fmtMoney(sizing.capitalizedInterestReserve)} financed interest reserve`
      : null,
    Number(sizing.financedClosingCosts) > 0
      ? `${fmtMoney(sizing.financedClosingCosts)} financed closing costs`
      : null,
  ].filter(Boolean);
  return parts.length ? <>, including {parts.join(" and ")}</> : null;
};

/** The calculated side of the last priced run, in words. */
const pricedLine = (sizing, isAmount) => {
  if (!sizing) return null;
  if (isAmount && sizing.sizerInitialRequestedLeverage != null) {
    return (
      <>
        Last priced at <b>{fmtPct(sizing.sizerInitialRequestedLeverage, 1)}</b> leverage
        {sizing.leverageBasisAmount ? <> of {fmtMoney(sizing.leverageBasisAmount)}</> : null}.
      </>
    );
  }
  if (!isAmount && sizing.baseLoanAmount != null) {
    return (
      <>
        Last priced at a <b>{fmtMoney(sizing.baseLoanAmount)}</b> initial loan amount
        {includedLine(sizing)}
        .
      </>
    );
  }
  return null;
};

export default function LeverageOrAmountField({ isPurchase, sizing, sx }) {
  const form = useForm();
  const { input: sizeBy } = useField(SIZE_BY_FIELD, { subscription: { value: true } });
  const mode = sizeBy.value === SIZE_BY.AMOUNT ? SIZE_BY.AMOUNT : SIZE_BY.LEVERAGE;
  const isAmount = mode === SIZE_BY.AMOUNT;

  const switchTo = (next) => {
    if (next === mode) return;
    form.batch(() => {
      form.change(SIZE_BY_FIELD, next);
      // One answer only — the other input is cleared so it is never posted.
      if (next === SIZE_BY.AMOUNT) form.change("sizerInitialRequestedLeverage", undefined);
      else form.change("baseLoanAmount", undefined);
    });
  };

  const basis = isPurchase
    ? "the as-is value or purchase price (whichever is lower)"
    : "the as-is value";

  return (
    <Box
      sx={{
        p: { xs: "14px", md: "16px 18px" },
        border: `1px solid ${SZ.line}`,
        borderRadius: SZ.radius,
        background: SZ.bg,
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "minmax(0, 7fr) minmax(0, 5fr)" },
        columnGap: { xs: "18px", md: "20px" },
        rowGap: "14px",
        alignItems: "start",
        ...sx,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <FieldLabel sx={{ color: SZ.ink700 }}>{QUESTION}</FieldLabel>
        <Box
          role="radiogroup"
          aria-label={QUESTION}
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: "8px",
          }}
        >
          <ChoiceCard
            on={!isAmount}
            glyph="%"
            title="Leverage"
            desc={isPurchase ? "% of as-is value or price" : "% of as-is value"}
            onClick={() => switchTo(SIZE_BY.LEVERAGE)}
          />
          <ChoiceCard
            on={isAmount}
            glyph="$"
            title="Loan amount"
            desc="Dollar amount"
            onClick={() => switchTo(SIZE_BY.AMOUNT)}
          />
        </Box>
      </Box>

      <Box sx={{ minWidth: 0 }}>
        {isAmount ? (
          <>
            <FieldLabel sx={{ color: SZ.ink700 }}>Initial loan amount</FieldLabel>
            <RffMoney name="baseLoanAmount" placeholder="e.g. 450,000" inputSx={INPUT_SX} />
          </>
        ) : (
          <>
            <FieldLabel sx={{ color: SZ.ink700 }}>Requested initial leverage</FieldLabel>
            <RffPercent
              name="sizerInitialRequestedLeverage"
              factor={100}
              placeholder="e.g. 75"
              inputSx={INPUT_SX}
            />
          </>
        )}
      </Box>

      <InfoNote>
        {isAmount ? (
          <>
            The amount funded at closing, including any financed interest reserve and
            financed closing costs. The <b>holdback</b> and <b>total loan amount</b> are
            calculated from it.
          </>
        ) : (
          <>
            The percent of {basis} funded at closing. The <b>initial loan amount</b>,{" "}
            <b>holdback</b> and <b>total loan amount</b> are calculated from it. A financed
            interest reserve and financed closing costs are added to the initial loan amount.
          </>
        )}
        {pricedLine(sizing, isAmount) && <> {pricedLine(sizing, isAmount)}</>}
      </InfoNote>
    </Box>
  );
}
